import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';
import CatalogPageLight from '@/pages/CatalogPageLight';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useImageCache } from '@/context/ImageCacheContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, ArrowLeft, Grid3X3, Package, X, Star, Share2, Printer, MessageCircle, FileText } from 'lucide-react';
import PartCard from '@/components/PartCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom hook for debounced value
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Animated Category Card Component with lazy image loading
const CategoryCard = ({ category, onClick, isAllProducts, isAdmin, onShare }) => {
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  // Intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Only load images when visible — uses batch API for efficiency
  useEffect(() => {
    if (!isVisible) return;
    
    const loadImages = async () => {
      if (category.sample_images && category.sample_images.length > 0) {
        const imagesToLoad = category.sample_images.slice(0, 4);
        
        try {
          // Single batch call instead of 4 individual requests
          const response = await axios.post(`${API}/images/batch`, imagesToLoad, { timeout: 15000 });
          const imageMap = response.data || {};
          const results = imagesToLoad.map(img => imageMap[img] || null).filter(Boolean);
          setImageUrls(results);
        } catch (error) {
          console.error('Error loading category images:', error);
        }
      }
      setLoading(false);
    };
    
    loadImages();
  }, [isVisible, category.sample_images]);

  const gridImages = imageUrls.length > 0 
    ? [...imageUrls.slice(0, 4), null, null, null, null].slice(0, 4)
    : [null, null, null, null];

  return (
    <div className="relative">
      {isAdmin && (
        <button
          type="button"
          data-testid={`category-share-btn-${category.name}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Defer state update one tick so the originating tap event
            // can finish bubbling BEFORE the modal mounts. Without this,
            // the same tap that opens the modal also lands on the
            // modal's backdrop on iOS Safari and instantly closes it.
            setTimeout(() => { onShare && onShare(category); }, 0);
          }}
          title={`Share ${category.name} catalogue`}
          className="absolute top-2 right-2 z-20 flex items-center justify-center w-9 h-9 rounded-full bg-white/10 backdrop-blur-md text-white/80 hover:bg-[#FFC75F] hover:text-slate-900 border border-white/15 transition-all shadow-lg"
        >
          <Share2 className="w-4 h-4" />
        </button>
      )}
      <div
        ref={cardRef}
        onClick={onClick}
        className={`group relative rounded-2xl border bg-white/[0.03] backdrop-blur-sm overflow-hidden cursor-pointer transition-all duration-300 hover:bg-white/[0.06] hover:-translate-y-0.5 ${
          isAllProducts
            ? 'border-[#FFC75F]/40 ring-1 ring-[#FFC75F]/20 shadow-[0_0_28px_rgba(255,199,95,0.12)]'
            : 'border-white/10 hover:border-white/25'
        }`}
      >
        {/* Brand-accent underline grows on hover (mirrors BrandLandingPage tiles) */}
        <div className={`absolute bottom-0 left-0 h-[2px] transition-all duration-500 z-[1] ${isAllProducts ? 'w-full bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F]' : 'w-10 bg-white/30 group-hover:w-full'}`} />

        {/* Photo stage — light surface so product images look natural,
            with a thin top/side inset so the dark card-border still frames it. */}
        <div className="aspect-square bg-gradient-to-b from-white to-[#F5F2EC] relative overflow-hidden rounded-t-2xl m-1 mb-0">
          {!isVisible || loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#FFC75F] rounded-full animate-spin" />
            </div>
          ) : imageUrls.length > 0 ? (
            <div className="grid grid-cols-2 grid-rows-2 w-full h-full gap-1 p-1.5">
              {gridImages.map((img, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-md overflow-hidden flex items-center justify-center ring-1 ring-slate-100"
                >
                  {img ? (
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                    />
                  ) : (
                    <Package className="w-8 h-8 text-slate-200" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Grid3X3 className="w-16 h-16 text-slate-200" />
            </div>
          )}

          {isAllProducts && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-gradient-to-r from-[#FF5A5F] to-[#FFC75F] text-slate-900 text-xs font-semibold border-0 shadow-md">
                <Star className="w-3 h-3 mr-1" />
                All
              </Badge>
            </div>
          )}
        </div>

      <div className={`px-4 py-3 ${isAllProducts ? 'bg-[#FFC75F]/[0.08]' : 'bg-white/[0.02]'}`}>
        <h3 className={`text-base sm:text-lg font-heading font-bold tracking-tight truncate transition-colors ${
          isAllProducts ? 'text-[#FFC75F]' : 'text-white group-hover:text-[#FFC75F]'
        }`}>
          {category.name}
        </h3>
        <p className="text-sm text-white/45 mt-0.5">
          {category.parts_count.toLocaleString()} {category.parts_count === 1 ? 'item' : 'items'}
        </p>
      </div>
      </div>
    </div>
  );
};

// Admin-only modal: choose how to share a category catalogue (WhatsApp or Print)
const ShareCatalogueModal = ({ category, token, loading, setLoading, onClose }) => {
  const isAllProducts = !!(category.is_all_products || category.name === 'All Products');
  // Resolve the active master category (Eicher / Swaraj Mazda / …) so the
  // generated PDF carries ONLY that brand's parts. Falls back to no scope
  // when the user is viewing the global "All Products" landing.
  const _activeBrand = (() => {
    try {
      const u = new URL(window.location.href);
      const fromUrl = u.searchParams.get('brand');
      if (fromUrl && fromUrl !== 'All Products') return fromUrl;
    } catch (e) { /* ignore */ }
    const fromStore = (localStorage.getItem('selectedCategory') || '').trim();
    return fromStore && fromStore !== 'All Products' ? fromStore : '';
  })();
  const generate = async ({ dense = false } = {}) => {
    setLoading(true);
    try {
      const brandQs = _activeBrand
        ? `brand=${encodeURIComponent(_activeBrand)}`
        : '';
      const denseQs = dense ? 'dense=true' : '';
      const qs = [brandQs, denseQs].filter(Boolean).join('&');
      const suffix = qs ? `?${qs}` : '';
      const url = isAllProducts
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/catalog/all-products/generate-pdf${suffix}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/catalog/${encodeURIComponent(category.name)}/generate-pdf${suffix}`;
      const { data } = await axios.post(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: isAllProducts ? 600000 : 90000, // up to 10 min for full catalog
        }
      );
      return data; // { pdf_url, parts_count, pages, file_name }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to generate catalogue PDF';
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = async () => {
    const data = await generate();
    if (!data?.pdf_url) return;

    const fileName = data.file_name || `${category.name.replace(/[^a-zA-Z0-9]+/g, '_')}_catalogue.pdf`;
    const captionText =
      `*${category.name} — Oswal Moto Corp*\n\n` +
      `Sharing our latest *${category.name}* product catalogue ` +
      `(${data.parts_count} items, ${data.pages} pages).\n\n` +
      `Visit our portal: www.jaiparasapp.in`;

    // WhatsApp's native attachment limit is ~16 MB; anything close to that
    // will silently fail to share — so we route the user to a download +
    // manual-attach flow instead of opening an irrelevant browser window.
    const HARD_LIMIT_MB = 15;

    // Helper: copy text to clipboard with a graceful fallback.
    const copyToClipboard = async (text) => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (_) { /* fall through */ }
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        return true;
      } catch (_) {
        return false;
      }
    };

    try {
      const resp = await fetch(data.pdf_url, { credentials: 'omit' });
      if (!resp.ok) throw new Error('PDF download failed');
      const blob = await resp.blob();
      const sizeMB = blob.size / (1024 * 1024);
      const file = new File([blob], fileName, { type: 'application/pdf' });

      // Path 1: small PDF + native file-share API → one-tap share with caption.
      if (
        sizeMB < HARD_LIMIT_MB &&
        typeof navigator !== 'undefined' &&
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        navigator.share
      ) {
        await navigator.share({
          files: [file],
          title: `${category.name} — Oswal Moto Corp`,
          text: captionText,
        });
        toast.success('Catalogue shared');
        onClose();
        return;
      }

      // Path 2: large file (or no native file-share). Auto-copy the caption
      // to the clipboard, then open the PDF in a new tab so the user can
      // share it natively from the browser's PDF viewer (which calls
      // through to WhatsApp on mobile and to WhatsApp Web on desktop).
      // When they pick WhatsApp, they just paste the caption as the message.
      const copied = await copyToClipboard(captionText);
      window.open(data.pdf_url, '_blank', 'noopener');

      toast.success(
        copied
          ? `Caption copied to clipboard. PDF opened in a new tab — share it on WhatsApp and paste the caption.`
          : `PDF opened in a new tab — share it on WhatsApp. Caption could not be auto-copied; please type a short message.`,
        { duration: 7000 }
      );
      onClose();
    } catch (err) {
      if (err && err.name === 'AbortError') return;  // user cancelled
      toast.error('Could not prepare the catalogue. Try again.');
    }
  };

  // 2026-08-03 · Small helper so the pre-opened tab isn't a jarring
  // blank page during the ~30 s PDF-generation wait. Renders a plain
  // "Generating catalogue…" screen that the newly-arrived PDF navigation
  // then replaces. Also guards against `null` refs from popup blockers.
  const paintLoadingTab = (win, label) => {
    if (!win || win.closed) return;
    try {
      win.document.open();
      win.document.write(`<!DOCTYPE html><html><head><title>${label} — Oswal Moto Corp</title>
        <meta charset="utf-8"/>
        <style>
          body{margin:0;background:#0f172a;color:#e2e8f0;font-family:system-ui,-apple-system,sans-serif;
               display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;text-align:center}
          .spinner{width:48px;height:48px;border:3px solid #334155;border-top-color:#FFC75F;border-radius:50%;
                   animation:spin .9s linear infinite;margin-bottom:20px}
          @keyframes spin{to{transform:rotate(360deg)}}
          h1{font-weight:600;font-size:20px;letter-spacing:.3px;margin:0 0 6px}
          p{margin:0;color:#94a3b8;font-size:14px}
        </style></head><body>
        <div class="spinner"></div>
        <h1>Preparing your catalogue…</h1>
        <p>This may take up to a minute for the full catalogue. Please don't close this tab.</p>
      </body></html>`);
      win.document.close();
    } catch (_) { /* cross-origin protections may block – harmless */ }
  };

  // "PDF" — Save/download the standard 12-per-page catalogue.
  // Open tab immediately during user gesture to avoid popup blocker.
  const handleSavePdf = async () => {
    // Drop `noopener` — modern browsers return `null` from window.open()
    // when noopener is passed, which broke the new-tab navigation
    // (the code below would fall through to navigating the *current*
    // tab instead). Safe because we're only opening our own trusted
    // PDF URL, not user-supplied content.
    const win = window.open('', '_blank');
    paintLoadingTab(win, 'Catalogue');
    const data = await generate();
    if (!data?.pdf_url) {
      if (win && !win.closed) win.close();
      return;
    }
    if (win && !win.closed) {
      win.location.href = data.pdf_url;
    } else {
      // Popup blocked — fall back to opening in current tab as a last resort.
      window.location.href = data.pdf_url;
    }
    toast.success('Catalogue PDF opened in a new tab.');
    onClose();
  };

  // "Print" — 15-per-page dense variant; auto-fires print dialog.
  const handleDensePrint = async () => {
    const win = window.open('', '_blank');
    paintLoadingTab(win, 'Print Catalogue');
    const data = await generate({ dense: true });
    if (!data?.pdf_url) {
      if (win && !win.closed) win.close();
      return;
    }
    if (win && !win.closed) {
      win.location.href = data.pdf_url;
      setTimeout(() => {
        try { win.focus(); win.print(); } catch (_) { /* viewer blocked */ }
      }, 1500);
    } else {
      // Popup blocked — fall back to opening in current tab.
      window.location.href = data.pdf_url;
    }
    toast.success('Print-ready catalogue (15 items/page) opened.');
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-catalogue-modal"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-red-700 to-red-900 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Share Catalogue</h3>
              <p className="text-red-100 text-sm">{category.name}</p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-white/80 hover:text-white p-1 disabled:opacity-30"
              data-testid="share-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            Generate a branded PDF catalogue with all{' '}
            <strong>{category.parts_count?.toLocaleString()}</strong>{' '}
            {category.parts_count === 1 ? 'item' : 'items'}{' '}
            {isAllProducts ? 'across every category' : 'in this category'}.
          </p>
          {isAllProducts && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-2">
              <strong>Heads-up:</strong> The full catalogue contains thousands of parts and may take a few minutes to generate. The PDF will be sectioned by category with a divider page before each one.
            </div>
          )}

          <button
            onClick={handleWhatsApp}
            disabled={loading}
            data-testid="share-via-whatsapp-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold transition-all"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <MessageCircle className="w-5 h-5" />
            )}
            <span className="flex-1 text-left">Share on WhatsApp</span>
          </button>

          <button
            onClick={handleSavePdf}
            disabled={loading}
            data-testid="share-pdf-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 font-semibold transition-all"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FileText className="w-5 h-5" />
            )}
            <span className="flex-1 text-left">PDF <span className="text-xs font-normal text-red-500/70 ml-1">· 12 / page</span></span>
          </button>

          <button
            onClick={handleDensePrint}
            disabled={loading}
            data-testid="share-print-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold transition-all"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Printer className="w-5 h-5" />
            )}
            <span className="flex-1 text-left">Print <span className="text-xs font-normal text-slate-300 ml-1">· 15 / page</span></span>
          </button>

          {loading && (
            <p className="text-xs text-center text-gray-500 pt-2">
              Generating PDF… can take 5–30 seconds for large categories.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const CatalogPage = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  const ADMIN_MOBILES = ['9999313175', '9999313176', '9810050321'];
  const isAdmin = !!(
    user && (
      user.is_admin ||
      user.is_staff_user ||
      user.role === 'salesman' ||
      user.role === 'admin' ||
      ADMIN_MOBILES.includes(String(user.mobile || ''))
    )
  );

  // Catalog share state
  const [shareCategory, setShareCategory] = useState(null); // { name, parts_count } or null
  const [shareLoading, setShareLoading] = useState(false);

  // Image prefetching for performance
  const { prefetchImages } = useImageCache();
  
  // View states
  const [viewMode, setViewMode] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partsLoading, setPartsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalParts, setTotalParts] = useState(0);
  const ITEMS_PER_LOAD = 30;
  
  // Refs
  const searchResultsRef = useRef(null);
  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const currentQueryRef = useRef('');
  const initialLoadDone = useRef(false);
  
  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 250);
  // 2026-05: race-guard for the category-paged fetchParts so a slow
  // short-prefix can't overwrite a fast long-prefix paged result.
  const fetchPartsRequestIdRef = useRef(0);

  // Load categories on mount - only once
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchCategories();
    }
  }, []);

  // Re-fetch the categories grid whenever the active master category
  // (?brand=) changes — otherwise switching brands from the nav shows
  // the previous brand's sub-categories + counts until a hard refresh.
  const brandParamFromUrl = searchParams.get('brand') || '';
  // `?edit=<jp_part_no>` triggers the matching card's edit dialog to open
  // automatically — used by the Missing-Images staff panel to deep-link
  // straight into the existing image-upload + AI-polish flow.
  const editJpFromUrl = (searchParams.get('edit') || '').trim();
  useEffect(() => {
    if (!initialLoadDone.current) return;  // mount-effect handles first load
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandParamFromUrl]);

  // Handle URL params - only on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    
    if (searchParam) {
      setSearchQuery(decodeURIComponent(searchParam));
      setViewMode('categories');
    } else if (categoryParam) {
      setSelectedCategory(decodeURIComponent(categoryParam));
      setViewMode('products');
    }
  }, []); // Empty deps - only run once

  // Live search for parts from categories view
  useEffect(() => {
    if (debouncedSearchQuery.trim() && viewMode === 'categories' && token) {
      setSearchResults([]);
      searchParts(debouncedSearchQuery.trim());
    } else if (!debouncedSearchQuery.trim() && viewMode === 'categories') {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery, viewMode, token]);

  // Auto-scroll to search results
  useEffect(() => {
    if (searchResults.length > 0 && searchResultsRef.current) {
      const timer = setTimeout(() => {
        searchResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchResults.length > 0]);

  // Fetch parts when category changes - reset and load fresh
  useEffect(() => {
    if (selectedCategory && viewMode === 'products' && token) {
      setParts([]);
      setSkip(0);
      setHasMore(true);
      fetchParts(true);
    }
  }, [selectedCategory, viewMode, token, debouncedSearchQuery]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current || viewMode !== 'products') return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !partsLoading && !loadingMore) {
          fetchParts(false);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );
    
    observer.observe(loadMoreRef.current);
    observerRef.current = observer;
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, partsLoading, loadingMore, viewMode]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Scope the categories grid (and the "All Products" count card)
      // to the master category the user selected on the home page —
      // otherwise the grid mixes sub-categories + parts counts from all
      // active brands and "All Products" shows the global total.
      const selectedBrand =
        searchParams.get('brand') || localStorage.getItem('selectedCategory') || '';
      const brandParam =
        selectedBrand && selectedBrand !== 'All Products'
          ? `?brand=${encodeURIComponent(selectedBrand)}`
          : '';
      const response = await axios.get(`${API}/categories/with-images${brandParam}`);
      const cats = response.data.categories || [];
      setCategories(cats);
      if (cats.length > 0) {
        // Cache scoped per-brand so switching brands doesn't show the
        // wrong brand's cached grid during the network round-trip.
        const cacheKey = selectedBrand && selectedBrand !== 'All Products'
          ? `cached_categories::${selectedBrand}`
          : 'cached_categories';
        localStorage.setItem(cacheKey, JSON.stringify(cats));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      const selectedBrand =
        searchParams.get('brand') || localStorage.getItem('selectedCategory') || '';
      const cacheKey = selectedBrand && selectedBrand !== 'All Products'
        ? `cached_categories::${selectedBrand}`
        : 'cached_categories';
      const cached = localStorage.getItem(cacheKey) || localStorage.getItem('cached_categories');
      if (cached) {
        try {
          setCategories(JSON.parse(cached));
        } catch (e) { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const searchParts = async (query) => {
    if (!token) return;
    
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    
    const abortController = new AbortController();
    searchAbortRef.current = abortController;
    
    const thisQuery = query.trim();
    const thisRequestId = ++searchRequestIdRef.current;
    currentQueryRef.current = thisQuery;
    
    setIsSearching(true);
    try {
      // Restrict search to the currently-selected master brand so the
      // Eicher catalog doesn't surface Mazda/Tata items (and vice versa).
      // Mirrors the HomePage hero search behaviour.
      const selectedBrand =
        searchParams.get('brand') || localStorage.getItem('selectedCategory') || '';
      const brandParam =
        selectedBrand && selectedBrand !== 'All Products'
          ? `&brand=${encodeURIComponent(selectedBrand)}`
          : '';
      const response = await axios.get(
        `${API}/categories/All%20Products/parts?search=${encodeURIComponent(thisQuery)}&limit=50${brandParam}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );
      
      if (thisRequestId !== searchRequestIdRef.current || currentQueryRef.current !== thisQuery) {
        return;
      }
      
      const searchPartsData = response.data.parts || [];
      setSearchResults(searchPartsData);
      
      const imageFilenames = searchPartsData.filter(p => p.image).map(p => p.image);
      if (imageFilenames.length > 0 && prefetchImages) {
        prefetchImages(imageFilenames);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      if (thisRequestId === searchRequestIdRef.current) {
        console.error('Error searching parts:', error);
        setSearchResults([]);
      }
    } finally {
      if (thisRequestId === searchRequestIdRef.current) {
        setIsSearching(false);
      }
    }
  };

  const fetchParts = useCallback(async (reset = false) => {
    if (!selectedCategory || !token) return;
    if ((partsLoading || loadingMore) && !reset) return;

    const thisRequestId = ++fetchPartsRequestIdRef.current;
    const thisSearch = debouncedSearchQuery.trim();
    const currentSkip = reset ? 0 : skip;
    
    if (reset) {
      setPartsLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams({
        skip: currentSkip.toString(),
        limit: ITEMS_PER_LOAD.toString(),
      });
      
      if (thisSearch) {
        params.append('search', thisSearch);
      }

      // Scope the catalog to the master category the user selected on the home page.
      // Prefer an explicit ?brand= URL param, fall back to the stored selection.
      const selectedBrand =
        searchParams.get('brand') || localStorage.getItem('selectedCategory') || '';
      if (selectedBrand && selectedBrand !== 'All Products') {
        params.append('brand', selectedBrand);
      }

      const response = await axios.get(
        `${API}/categories/${encodeURIComponent(selectedCategory)}/parts?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Guard: only the latest fetch's result is applied.
      if (thisRequestId !== fetchPartsRequestIdRef.current) return;
      if (thisSearch !== debouncedSearchQuery.trim()) return;

      const partsData = response.data.parts || [];
      const total = response.data.total || 0;
      
      if (reset) {
        setParts(partsData);
        setSkip(ITEMS_PER_LOAD);
      } else {
        setParts(prev => [...prev, ...partsData]);
        setSkip(prev => prev + ITEMS_PER_LOAD);
      }
      
      setTotalParts(total);
      setHasMore(currentSkip + partsData.length < total);
      
      const imageFilenames = partsData.filter(p => p.image).map(p => p.image);
      if (imageFilenames.length > 0 && prefetchImages) {
        prefetchImages(imageFilenames);
      }
    } catch (error) {
      if (thisRequestId === fetchPartsRequestIdRef.current) {
        console.error('Error fetching parts:', error);
      }
    } finally {
      if (thisRequestId === fetchPartsRequestIdRef.current) {
        setPartsLoading(false);
        setLoadingMore(false);
      }
    }
  }, [selectedCategory, token, skip, partsLoading, loadingMore, debouncedSearchQuery, prefetchImages]);

  const handleProductUpdated = useCallback(() => {
    // Refresh current page without resetting
    fetchParts(true);
  }, [fetchParts]);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setViewMode('products');
    setSearchQuery('');
    setParts([]);
    setSkip(0);
    setHasMore(true);
    setSearchParams({ category: categoryName });
  };

  const handleBackToCategories = () => {
    setViewMode('categories');
    setSelectedCategory(null);
    setParts([]);
    setSearchQuery('');
    setSkip(0);
    setSearchParams({});
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    if (viewMode === 'products') {
      setParts([]);
      setSkip(0);
      setHasMore(true);
    }
  };

  // Render the original light-themed page when the customer chose light.
  // Placed AFTER every hook so Rules-of-Hooks order stays stable.
  if (theme === 'light') return <CatalogPageLight />;

  // Categories View
  if (viewMode === 'categories') {
    return (
      <div className="min-h-screen text-white relative overflow-hidden" style={{ backgroundColor: '#0B1220' }}>
        {/* Ambient gradient + soft orbs (same recipe as /search/:brand) */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(155deg, #0F1320 0%, #0B1220 45%, #07101C 100%)' }}
        />
        <div aria-hidden className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-70"
             style={{ background: 'radial-gradient(circle, rgba(244,162,97,0.18) 0%, rgba(255,138,90,0.08) 45%, transparent 75%)', filter: 'blur(72px)' }} />
        <div aria-hidden className="absolute -bottom-40 -right-24 w-[640px] h-[640px] rounded-full pointer-events-none opacity-50"
             style={{ background: 'radial-gradient(circle, rgba(56,114,224,0.18) 0%, rgba(56,114,224,0.07) 45%, transparent 78%)', filter: 'blur(90px)' }} />

        <div className="container mx-auto px-4 max-w-full py-8 md:py-12 relative">
          {/* Header */}
          <div className="mb-7 md:mb-10 opacity-0 animate-[fadeUp_.7s_ease_.1s_forwards]">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold tracking-tight text-white">
              Browse by{' '}
              <span className="bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F] bg-clip-text text-transparent">
                Category.
              </span>
            </h1>
            <p className="text-white/60 mt-2 text-sm md:text-base">Curated parts catalogue — every category, fully stocked, ready to dispatch.</p>
          </div>

          {/* Search Bar */}
          <div className="mb-7 opacity-0 animate-[fadeUp_.8s_ease_.2s_forwards]">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="Search by part name, part number, OE number…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="pl-12 pr-10 bg-white/[0.04] border border-white/10 backdrop-blur-md text-white placeholder:text-white/40 text-base h-12 rounded-xl focus-visible:ring-1 focus-visible:ring-[#FFC75F]/50 focus-visible:border-[#FFC75F]/50"
                data-testid="catalog-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="text-xs text-white/40 mt-2 inline-flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live search — results appear as you type
            </p>
          </div>

          {/* Show search results if searching */}
          {searchQuery.trim() ? (
            <div ref={searchResultsRef}>
              {isSearching ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FFC75F]" />
                  <span className="ml-3 text-white/60">Searching…</span>
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  <p className="text-sm text-white/50 mb-4">
                    Found {searchResults.length} products matching &quot;{searchQuery}&quot;
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                    {searchResults.map((part) => (
                      <PartCard key={`${part.jp_part_no}-${part.image || 'no-img'}`} part={part} onProductUpdated={handleProductUpdated} autoOpenEdit={!!editJpFromUrl && part.jp_part_no === editJpFromUrl} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <Package className="w-16 h-16 mx-auto text-white/20 mb-4" />
                  <p className="text-white/70">No products found matching &quot;{searchQuery}&quot;</p>
                  <p className="text-sm text-white/40 mt-2">
                    Try different keywords or check your spelling
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Loading State */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FFC75F]" />
                  <span className="ml-3 text-white/60">Loading categories…</span>
                </div>
              ) : (
                <>
                  <p className="text-sm text-white/50 mb-4">
                    {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {categories.map((category, index) => (
                      <CategoryCard
                        key={category.name}
                        category={category}
                        onClick={() => handleCategoryClick(category.name)}
                        isAllProducts={category.is_all_products || (index === 0 && category.name === "All Products")}
                        isAdmin={isAdmin}
                        onShare={(cat) => setShareCategory(cat)}
                      />
                    ))}
                  </div>

                  {categories.length === 0 && (
                    <div className="text-center py-20">
                      <Grid3X3 className="w-16 h-16 mx-auto text-white/20 mb-4" />
                      <p className="text-white/70">No categories found</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Admin: Share-Catalogue Modal (Categories View) */}
        {shareCategory && (
          <ShareCatalogueModal
            category={shareCategory}
            token={token}
            loading={shareLoading}
            setLoading={setShareLoading}
            onClose={() => { if (!shareLoading) setShareCategory(null); }}
          />
        )}

        {/* Local entry-animation keyframes — match BrandLandingPage / HomePage. */}
        <style>{`
          @keyframes fadeUp { 0% { opacity: 0; transform: translateY(18px);} 100% { opacity: 1; transform: translateY(0);} }
          @media (prefers-reduced-motion: reduce) { .min-h-screen * { animation: none !important; opacity: 1 !important; } }
        `}</style>
      </div>
    );
  }

  // Products View (when a category is selected) - with Infinite Scroll
  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ backgroundColor: '#0B1220' }}>
      {/* Ambient gradient backdrop — matches /search/:brand page */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
           style={{ background: 'linear-gradient(155deg, #0F1320 0%, #0B1220 45%, #07101C 100%)' }} />
      <div aria-hidden className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-70"
           style={{ background: 'radial-gradient(circle, rgba(244,162,97,0.18) 0%, rgba(255,138,90,0.08) 45%, transparent 75%)', filter: 'blur(72px)' }} />
      <div aria-hidden className="absolute -bottom-40 -right-24 w-[640px] h-[640px] rounded-full pointer-events-none opacity-50"
           style={{ background: 'radial-gradient(circle, rgba(56,114,224,0.18) 0%, rgba(56,114,224,0.07) 45%, transparent 78%)', filter: 'blur(90px)' }} />

      <div className="container mx-auto px-4 max-w-full py-8 md:py-12 relative">
        {/* Header with Back Button */}
        <div className="mb-7 md:mb-10 opacity-0 animate-[fadeUp_.7s_ease_.1s_forwards]">
          <Button
            variant="ghost"
            onClick={handleBackToCategories}
            className="mb-3 -ml-2 text-white/70 hover:text-white hover:bg-white/[0.08] rounded-full border border-white/10 backdrop-blur-sm h-9 px-3"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold tracking-tight text-white">
            <span className="bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F] bg-clip-text text-transparent">
              {selectedCategory}
            </span>
          </h1>
          <p className="text-white/55 mt-2 text-sm md:text-base">
            {totalParts.toLocaleString()} products in this category
          </p>
        </div>

        {/* Search */}
        <div className="mb-7 opacity-0 animate-[fadeUp_.8s_ease_.2s_forwards]">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              type="text"
              placeholder="Search by part name, part number, OE number…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="pl-12 pr-10 bg-white/[0.04] border border-white/10 backdrop-blur-md text-white placeholder:text-white/40 text-base h-12 rounded-xl focus-visible:ring-1 focus-visible:ring-[#FFC75F]/50 focus-visible:border-[#FFC75F]/50"
              data-testid="catalog-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-white/40 mt-2 inline-flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live search — results filter as you type
          </p>
        </div>

        {/* Initial Loading State */}
        {partsLoading && parts.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FFC75F]" />
            <span className="ml-3 text-white/60">Loading products…</span>
          </div>
        ) : (
          <>
            {/* Products Grid with Infinite Scroll */}
            {parts.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                  {parts.map((part) => (
                    <PartCard key={`${part.jp_part_no}-${part.image || 'no-img'}`} part={part} onProductUpdated={handleProductUpdated} autoOpenEdit={!!editJpFromUrl && part.jp_part_no === editJpFromUrl} />
                  ))}
                </div>

                {/* Load More Trigger */}
                <div ref={loadMoreRef} className="py-8 flex items-center justify-center">
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-white/50">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Loading more products…</span>
                    </div>
                  )}
                  {!hasMore && parts.length > 0 && (
                    <p className="text-white/35 text-sm">
                      All {totalParts.toLocaleString()} products loaded
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto text-white/20 mb-4" />
                <p className="text-white/70">
                  {searchQuery ? `No products found matching "${searchQuery}"` : 'No products found in this category'}
                </p>
                {searchQuery && (
                  <p className="text-sm text-white/40 mt-2">
                    Try different keywords or clear the search
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Admin: Share-Catalogue Modal */}
      {shareCategory && (
        <ShareCatalogueModal
          category={shareCategory}
          token={token}
          loading={shareLoading}
          setLoading={setShareLoading}
          onClose={() => { if (!shareLoading) setShareCategory(null); }}
        />
      )}

      {/* fadeUp keyframes — must be declared on the products view too,
          otherwise the header (back button + title + search) stays at
          opacity-0 and is unclickable. */}
      <style>{`
        @keyframes fadeUp { 0% { opacity: 0; transform: translateY(18px);} 100% { opacity: 1; transform: translateY(0);} }
        @media (prefers-reduced-motion: reduce) { .min-h-screen * { animation: none !important; opacity: 1 !important; } }
      `}</style>
    </div>
  );
};

export default CatalogPage;
